import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  ArrowLeft,
  Instagram,
  ExternalLink,
  Copy,
  Check,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Info,
  Clock,
  Key,
  Settings,
  RefreshCw,
  Smartphone,
  Globe,
  Shield
} from "lucide-react";
import { toast } from "sonner";

export default function InstagramGuidePage() {
  const navigate = useNavigate();
  const [copiedText, setCopiedText] = useState(null);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.success("Copiado!");
    setTimeout(() => setCopiedText(null), 2000);
  };

  const steps = [
    {
      number: 1,
      title: "Criar conta de desenvolvedor no Facebook",
      duration: "5 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Primeiro, você precisa ter uma conta de desenvolvedor no Facebook (Meta).
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>Acesse <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">developers.facebook.com</a></li>
              <li>Clique em <strong>"Começar"</strong> ou <strong>"Get Started"</strong></li>
              <li>Faça login com sua conta do Facebook</li>
              <li>Aceite os termos de uso</li>
              <li>Confirme sua identidade (pode pedir verificação por SMS)</li>
            </ol>
          </div>

          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => window.open('https://developers.facebook.com', '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Abrir developers.facebook.com
          </Button>
        </div>
      )
    },
    {
      number: 2,
      title: "Criar um App no Facebook",
      duration: "3 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Agora você vai criar um "App" que será usado para acessar o Instagram.
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>No painel de desenvolvedor, clique em <strong>"Meus Apps"</strong> (ou "My Apps")</li>
              <li>Clique em <strong>"Criar App"</strong></li>
              <li>Selecione <strong>"Empresa"</strong> ou <strong>"Business"</strong> como tipo</li>
              <li>Dê um nome ao app (ex: "Integração Igreja")</li>
              <li>Informe um email de contato</li>
              <li>Clique em <strong>"Criar App"</strong></li>
            </ol>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 text-sm font-medium">Importante</p>
                <p className="text-amber-700 text-sm">
                  Se pedir "Página do Facebook vinculada", você pode criar uma página simples ou usar uma existente.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 3,
      title: "Adicionar o produto Instagram Basic Display",
      duration: "2 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Dentro do seu app, você precisa adicionar o produto do Instagram.
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>No painel do seu app, role para baixo até <strong>"Adicionar produtos"</strong></li>
              <li>Encontre <strong>"Instagram Basic Display"</strong></li>
              <li>Clique em <strong>"Configurar"</strong></li>
              <li>Clique em <strong>"Criar novo app"</strong> se solicitado</li>
            </ol>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-1" />
              <p className="text-xs text-green-700 font-medium">Instagram Basic Display</p>
              <p className="text-xs text-green-600">Use este</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center opacity-60">
              <Instagram className="h-6 w-6 text-gray-400 mx-auto mb-1" />
              <p className="text-xs text-gray-500 font-medium">Instagram Graph API</p>
              <p className="text-xs text-gray-400">Para contas business</p>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 4,
      title: "Configurar URLs de redirecionamento",
      duration: "2 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            O Facebook precisa saber para onde redirecionar após a autenticação.
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Configure estas URLs:</h4>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-blue-600 mb-1">OAuth Redirect URI:</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded text-sm border">
                    https://seu-dominio.com/auth/instagram/callback
                  </code>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard('https://seu-dominio.com/auth/instagram/callback', 'redirect')}
                  >
                    {copiedText === 'redirect' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-blue-600 mb-1">Deauthorize Callback URL:</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded text-sm border">
                    https://seu-dominio.com/auth/instagram/deauthorize
                  </code>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard('https://seu-dominio.com/auth/instagram/deauthorize', 'deauth')}
                  >
                    {copiedText === 'deauth' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs text-blue-600 mb-1">Data Deletion Request URL:</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded text-sm border">
                    https://seu-dominio.com/auth/instagram/delete
                  </code>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => copyToClipboard('https://seu-dominio.com/auth/instagram/delete', 'delete')}
                  >
                    {copiedText === 'delete' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-2">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 text-sm">
                <strong>Dica:</strong> Se estiver apenas testando, pode usar URLs fictícias como 
                <code className="bg-amber-100 px-1 rounded mx-1">https://localhost/callback</code>
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 5,
      title: "Adicionar usuário de teste do Instagram",
      duration: "2 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Enquanto o app não está em produção, você precisa adicionar sua conta do Instagram como "testador".
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>No menu lateral, vá em <strong>"Funções"</strong> → <strong>"Funções"</strong></li>
              <li>Role até <strong>"Testadores do Instagram"</strong></li>
              <li>Clique em <strong>"Adicionar testadores do Instagram"</strong></li>
              <li>Digite seu nome de usuário do Instagram (sem @)</li>
              <li>Clique em <strong>"Enviar"</strong></li>
            </ol>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex gap-2">
              <Smartphone className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-purple-800 text-sm font-medium">Aceitar convite no Instagram</p>
                <p className="text-purple-700 text-sm">
                  Abra o Instagram → Configurações → Segurança → Apps e sites → 
                  Convites de testador → Aceitar
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 6,
      title: "Gerar o Token de Acesso",
      duration: "3 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Agora você vai gerar o token que será usado no sistema.
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>No menu lateral do seu app, vá em <strong>"Instagram Basic Display"</strong></li>
              <li>Role até <strong>"Geração de token do usuário"</strong></li>
              <li>Encontre seu usuário de teste na lista</li>
              <li>Clique em <strong>"Gerar token"</strong></li>
              <li>Uma janela abrirá pedindo permissão - clique em <strong>"Permitir"</strong></li>
              <li>O token será exibido - <strong>COPIE E GUARDE EM LUGAR SEGURO</strong></li>
            </ol>
          </div>

          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-800 text-sm font-medium">Atenção!</p>
                <p className="text-red-700 text-sm">
                  O token só aparece UMA VEZ. Se perder, terá que gerar outro.
                  Ele expira em 60 dias.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex gap-2">
              <Key className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-800 text-sm font-medium">Exemplo de token:</p>
                <code className="text-green-700 text-xs break-all">
                  IGQVJYeUhxZ2NwbXFIRUNTX0RhdG9kZWxldGVfYWNjZXNz...
                </code>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      number: 7,
      title: "Configurar no Sistema",
      duration: "1 min",
      content: (
        <div className="space-y-4">
          <p className="text-gray-600">
            Agora é só colocar o token no sistema!
          </p>
          
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Passo a passo:</h4>
            <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
              <li>Vá em <strong>Agenda & Programação</strong></li>
              <li>Role até a seção <strong>Instagram</strong></li>
              <li>Preencha o campo <strong>Usuário</strong> (seu @)</li>
              <li>Cole o token no campo <strong>Token de Acesso</strong></li>
              <li>Selecione os tipos de conteúdo (Stories, Publicações, Reels)</li>
              <li>Clique em <strong>Salvar</strong></li>
              <li>Clique em <strong>Sincronizar</strong></li>
            </ol>
          </div>

          <Button 
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
            onClick={() => navigate('/agenda-config')}
          >
            <Settings className="h-4 w-4 mr-2" />
            Ir para Agenda & Programação
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center">
              <Instagram className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Integração Instagram</h1>
              <p className="text-xs text-muted-foreground">Guia passo a passo</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Intro */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
                <Instagram className="h-6 w-6 text-pink-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">
                  Importe conteúdo do Instagram
                </h2>
                <p className="text-gray-600 text-sm">
                  Este guia vai te ajudar a conectar sua conta do Instagram ao sistema
                  para importar automaticamente suas publicações, stories e reels.
                </p>
                <div className="flex items-center gap-4 mt-3">
                  <Badge variant="outline" className="gap-1">
                    <Clock className="h-3 w-3" />
                    ~20 minutos
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <Shield className="h-3 w-3" />
                    Conta pessoal ou business
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">O que você vai precisar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <Globe className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Conta no Facebook</p>
                <p className="text-xs text-muted-foreground">Para acessar o portal de desenvolvedores</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center">
                <Instagram className="h-4 w-4 text-pink-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Conta no Instagram</p>
                <p className="text-xs text-muted-foreground">De onde virá o conteúdo</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <Smartphone className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-sm">Celular com Instagram</p>
                <p className="text-xs text-muted-foreground">Para aceitar o convite de testador</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Steps */}
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-gray-900 px-1">Passo a passo</h2>
          
          {steps.map((step, index) => (
            <Card key={step.number} className="overflow-hidden">
              <details className="group">
                <summary className="cursor-pointer list-none">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ 
                          background: `linear-gradient(135deg, hsl(${280 - index * 30}, 70%, 50%), hsl(${280 - index * 30 + 30}, 70%, 50%))` 
                        }}
                      >
                        {step.number}
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-base">{step.title}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-0.5">
                          <Clock className="h-3 w-3" />
                          {step.duration}
                        </CardDescription>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 transition-transform group-open:rotate-90" />
                    </div>
                  </CardHeader>
                </summary>
                <CardContent className="pt-0 pb-4">
                  <Separator className="mb-4" />
                  {step.content}
                </CardContent>
              </details>
            </Card>
          ))}
        </div>

        {/* Token Renewal */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Renovação do Token
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-800 mb-3">
              O token do Instagram expira em <strong>60 dias</strong>. Para renovar:
            </p>
            <ol className="list-decimal list-inside space-y-1 text-sm text-amber-700">
              <li>Volte ao portal de desenvolvedores do Facebook</li>
              <li>Vá em Instagram Basic Display</li>
              <li>Gere um novo token</li>
              <li>Atualize no sistema</li>
            </ol>
          </CardContent>
        </Card>

        {/* Help */}
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-3">
              Precisa de ajuda? Entre em contato com o suporte.
            </p>
            <Button variant="outline" onClick={() => navigate('/agenda-config')}>
              <Settings className="h-4 w-4 mr-2" />
              Ir para Configurações
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
